import { Activity, Heart, Home, Shield, Zap } from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import careTypeApi from "@/services/care-types.api";
import { getSpecialtiesRequest } from "@/store/reducers/specialtiesSlice";
import { selectSpecialties } from "@/store/selectors/specialtiesSelector";
import {
  Input,
  SpecialtySearchAutocomplete,
} from "@ews-admin/global-design-system";
import { useDispatch, useSelector } from "react-redux";
import { Facility } from "../../../../types/facility.definition";
import { SpecialtyCode } from "../../../../types/specialty.definition";
import type { CareType } from "../../../../webclients/care-types.client";

interface ServicesTabProps {
  facility: Facility;
  setFacility: React.Dispatch<React.SetStateAction<Facility>>;
}

// Icon mapping for care types
const CARE_TYPE_ICONS: {
  [key: string]: React.ComponentType<React.SVGProps<SVGSVGElement>>;
} = {
  ICU: Heart,
  VACCINATION: Shield,
  SURGERY: Activity,
  EMERGENCY: Zap,
  LONG_TERM_CARE: Home,
};

const DEFAULT_ICON = Activity;

export const ServicesTab: React.FC<ServicesTabProps> = ({
  facility,
  setFacility,
}) => {
  const { t, i18n } = useTranslation();
  const specialties = useSelector(selectSpecialties);
  const dispatch = useDispatch();
  const [careTypes, setCareTypes] = useState<CareType[]>([]);
  const [isLoadingCareTypes, setIsLoadingCareTypes] = useState(false);

  // Helper function to get translated care type label
  const getCareTypeLabel = (careType: CareType): string => {
    const translationKey = `facilitySettings.services.careTypes.${careType.code}`;
    const translatedLabel = t(translationKey);
    // If translation exists and is different from the key, use it
    if (translatedLabel !== translationKey) {
      return translatedLabel;
    }
    // Fallback to API label or code
    return careType.label || careType.code;
  };
  const handleCareTypeToggle = (careTypeId: number) => {
    setFacility((prev) => {
      const currentCareTypes = prev.careTypes || [];
      const isSelected = currentCareTypes.some((ct) => ct.id === careTypeId);

      if (isSelected) {
        // Remove care type
        return {
          ...prev,
          careTypes: currentCareTypes.filter((ct) => ct.id !== careTypeId),
        };
      } else {
        // Add care type - find from available care types
        const careType = careTypes.find((ct) => ct.id === careTypeId);
        if (careType) {
          return {
            ...prev,
            careTypes: [
              ...currentCareTypes,
              {
                id: careType.id,
                code: careType.code,
                label: careType.label,
              },
            ],
          };
        }
        return prev;
      }
    });
  };

  const fetchCareTypes = useCallback(async () => {
    try {
      setIsLoadingCareTypes(true);
      const data = await careTypeApi.getCareTypes(i18n.language);
      setCareTypes(data || []);
    } catch (error) {
      console.error("Error fetching care types:", error);
    } finally {
      setIsLoadingCareTypes(false);
    }
  }, [i18n.language]);

  const handleSpecialtiesChange = (
    specialties: { code: SpecialtyCode; label: string; id?: string }[],
  ) => {
    // Store the full specialty objects in facility state
    setFacility((prev) => ({
      ...prev,
      specialties: specialties,
    }));
  };

  // Convert facility specialties to the format expected by the component
  const selectedSpecialties = facility.specialties?.map((specialty) => {
    // If specialty is already an object with id, code, label
    if (typeof specialty === "object" && specialty !== null) {
      return {
        id: specialty.id || "",
        code: (specialty.code || "") as SpecialtyCode,
        label: specialty.label || "",
      };
    }

    // If specialty is a string (ID or code), create a basic object
    const specialtyStr = String(specialty);
    return {
      id: specialtyStr,
      code: specialtyStr as SpecialtyCode,
      label: specialtyStr, // This will be replaced with actual labels from the API
    };
  });
  useEffect(() => {
    if (specialties.length === 0) {
      dispatch(getSpecialtiesRequest());
    }
    fetchCareTypes();
  }, [dispatch, specialties.length, fetchCareTypes]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        {t("facilitySettings.services.description")}
      </p>

      {/* Healthcare Services Section */}
      <div className="space-y-4">
        <div className="flex items-center mb-4 space-x-3">
          <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-ews-primary-100">
            <Activity className="w-4 h-4 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">
            {t("facilitySettings.services.healthcareServices")}
          </h3>
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-neutral-700">
            {t("facilitySettings.services.selectServices")}
          </label>
          <p className="mb-4 text-xs text-neutral-500">
            {t("facilitySettings.services.selectServicesDescription")}
          </p>

          {isLoadingCareTypes ? (
            <div className="p-4 text-center text-gray-500">
              {t("facilitySettings.services.loadingCareTypes")}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {careTypes.map((careType) => {
                const IconComponent =
                  CARE_TYPE_ICONS[careType.code] || DEFAULT_ICON;
                const isSelected =
                  facility.careTypes?.some((ct) => ct.id === careType.id) ||
                  false;

                return (
                  <div
                    key={careType.id}
                    className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${isSelected
                        ? "border-primary-500 bg-ews-primary-50"
                        : "bg-white border-neutral-200 hover:border-neutral-300"
                      }`}
                    onClick={() =>
                      careType.id && handleCareTypeToggle(careType.id)
                    }
                  >
                    <div className="flex items-start space-x-3">
                      <div
                        className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? "bg-ews-primary-100" : "bg-neutral-100"
                          }`}
                      >
                        <IconComponent
                          className={`w-5 h-5 ${isSelected ? "text-primary-600" : "text-neutral-600"
                            }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <h4
                            className={`text-sm font-medium ${isSelected
                                ? "text-ews-primary-900"
                                : "text-neutral-900"
                              }`}
                          >
                            {getCareTypeLabel(careType)}
                          </h4>
                          {isSelected && (
                            <div className="w-2 h-2 rounded-full bg-primary-500"></div>
                          )}
                        </div>
                        <p
                          className={`mt-1 text-xs ${isSelected ? "text-primary-700" : "text-neutral-500"
                            }`}
                        >
                          {t("facilitySettings.services.code")} {careType.code}
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <Input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() =>
                            careType.id && handleCareTypeToggle(careType.id)
                          }
                          className="w-4 h-4 rounded text-primary-600 border-neutral-300 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Selected Care Types Summary */}
          {facility.careTypes && facility.careTypes.length > 0 && (
            <div className="p-3 mt-4 rounded-lg border bg-ews-primary-50 border-primary-200">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-primary-500"></div>
                <span className="text-sm font-medium text-ews-primary-800">
                  {t("facilitySettings.services.servicesSelected", {
                    count: facility.careTypes.length,
                  })}
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {facility.careTypes.map((careType) => (
                  <span
                    key={careType.id}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full text-ews-primary-800 bg-ews-primary-100"
                  >
                    {getCareTypeLabel(careType)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Specialties Section */}
      <div className="space-y-4">
        <SpecialtySearchAutocomplete
          specialties={specialties.map((specialty) => ({
            id: specialty.id as string,
            code: specialty.code,
            label: specialty.label,
          }))}
          title={t("facilitySettings.services.medicalSpecialties")}
          label={t("facilitySettings.services.selectSpecialties")}
          selectedSpecialties={selectedSpecialties}
          onSpecialtiesChange={handleSpecialtiesChange}
          placeholder={t("facilitySettings.services.specialtiesPlaceholder")}
          getSelectedCountText={(count: number) => {
            return t("facilitySettings.services.specialtiesSelected", {
              count: count,
            });
          }}
        />
      </div>
    </div>
  );
};
